'use client'

import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react'
import { SiteSettings } from '@/types/database'
import { usePathname } from 'next/navigation'
import { useLocale } from 'next-intl'
import config from '@/i18n/config'

// Define window augmentation for TypeScript
declare global {
  interface Window {
    __NEXT_THEME__?: {
      preference: 'light' | 'dark' | 'system',
      active: 'light' | 'dark'
    }
  }
}

// Define the context type
interface ThemeContextType {
  settings: SiteSettings
  theme: 'light' | 'dark' // Current active theme
  themePreference: 'light' | 'dark' | 'system' // User's preference
  setTheme: (theme: 'light' | 'dark' | 'system') => void // Function to change theme preference
}

// Create the context
const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

// Helper function to convert hex to HSL format for Tailwind opacity support
function hexToHsl(hex: string): string {
  try {
    // Ensure color is a valid hex code
    if (!/^#[0-9A-F]{6}$/i.test(hex)) {
      console.warn(`Invalid hex color format: ${hex}`);
      return '0 0% 0%'; // Default to black HSL
    }
    
    // Convert hex to RGB
    const r = parseInt(hex.slice(1, 3), 16) / 255;
    const g = parseInt(hex.slice(3, 5), 16) / 255;
    const b = parseInt(hex.slice(5, 7), 16) / 255;
    
    // Find max and min RGB components
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h = 0, s = 0, l = (max + min) / 2;

    if (max !== min) {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      
      switch (max) {
        case r: h = (g - b) / d + (g < b ? 6 : 0); break;
        case g: h = (b - r) / d + 2; break;
        case b: h = (r - g) / d + 4; break;
      }
      
      h = Math.round(h * 60);
    }
    
    s = Math.round(s * 100);
    l = Math.round(l * 100);

    return `${h} ${s}% ${l}%`;
  } catch (e) {
    console.error('Error converting hex to HSL:', e);
    return '0 0% 0%'; // Default to black HSL
  }
}

interface ThemeProviderProps {
  children: ReactNode
  initialSettings: SiteSettings
}

export function ThemeProvider({ children, initialSettings }: ThemeProviderProps) {
  const [settings] = useState<SiteSettings>(initialSettings)
  
  // Detect forced theme on home and products pages
  const pathname = usePathname()
  const locale = useLocale()
  
  // Split pathname into segments and filter out empty strings
  const pathSegments = pathname.split('/').filter(Boolean)
  
  // Get the products path for the current locale
  const productsPath = config.pathnames['/products'][locale as keyof typeof config.pathnames['/products']]
  const productsPathSegment = productsPath.split('/').filter(Boolean)[0] // Get the translated segment
  
  // Check if we're on the root locale page (e.g., /en or /sv)
  const isLocaleRoot = pathSegments.length === 1 && pathSegments[0] === locale
  
  // Check if we're on the products listing page using the translated path
  const isProductsListing = pathSegments.length === 2 && 
    pathSegments[0] === locale && 
    pathSegments[1] === productsPathSegment
  
  // Force dark theme only on locale root and products listing pages
  const forcedTheme: 'light' | 'dark' | undefined = 
    (isLocaleRoot || isProductsListing) ? 'dark' : undefined
  
  // Get initial values from window.__NEXT_THEME__ if available (SSR-safe)
  const initialTheme = typeof window !== 'undefined' && window.__NEXT_THEME__ 
    ? window.__NEXT_THEME__.active 
    : undefined;
    
  const initialPreference = typeof window !== 'undefined' && window.__NEXT_THEME__ 
    ? window.__NEXT_THEME__.preference
    : undefined;
  
  // Initialize with undefined to avoid hydration mismatches
  const [themePreference, setThemePreference] = useState<'light' | 'dark' | 'system' | undefined>(initialPreference)
  const [activeTheme, setActiveTheme] = useState<'light' | 'dark' | undefined>(initialTheme)
  const [mounted, setMounted] = useState(false)

  // Mark component as mounted (client-side only)
  useEffect(() => {
    setMounted(true)
    
    // Initialize theme state from localStorage or system preference
    // This code runs only on the client after hydration
    const storedPreference = localStorage.getItem('themePreference')
    
    // Set the theme preference
    if (storedPreference && ['light', 'dark', 'system'].includes(storedPreference)) {
      setThemePreference(storedPreference as 'light' | 'dark' | 'system')
    } else {
      setThemePreference('system') // Default if not in localStorage
    }
    
    // Determine the active theme based on preference or system
    let currentTheme: 'light' | 'dark';
    if (storedPreference === 'light' || storedPreference === 'dark') {
      currentTheme = storedPreference as 'light' | 'dark';
    } else {
      // System preference
      currentTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
    
    setActiveTheme(currentTheme);
  }, [])

  // Effect to determine and apply the active theme based on preference and system settings
  useEffect(() => {
    // Skip if preference not loaded yet or not mounted
    if (!themePreference || !mounted) return;
    
    let currentTheme: 'light' | 'dark';
    const systemPrefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches

    // Apply forced theme if on the home page or products listing
    if (forcedTheme) {
      currentTheme = forcedTheme
      // Debug logging for forced theme
      console.log('Applying forced theme:', {
        forcedTheme,
        isLocaleRoot,
        isProductsListing,
        pathname,
        locale
      })
    } else if (themePreference === 'system') {
      currentTheme = systemPrefersDark ? 'dark' : 'light'
    } else {
      currentTheme = themePreference
    }
    
    setActiveTheme(currentTheme)

    // Apply class to HTML element
    const root = document.documentElement
    root.classList.remove('light', 'dark')
    root.classList.add(currentTheme)

    // Apply CSS variables in HSL format for Tailwind opacity support
    if (settings.primary_color) {
      root.style.setProperty('--color-primary', hexToHsl(settings.primary_color))
    }
    
    // Only try to set gradient variables if they exist in settings
    if (settings.primary_gradient_from) {
      root.style.setProperty('--color-primary-gradient-from', hexToHsl(settings.primary_gradient_from))
    } else if (settings.primary_color) {
      // Fallback to primary color
      root.style.setProperty('--color-primary-gradient-from', hexToHsl(settings.primary_color))
    }
    
    if (settings.primary_gradient_to) {
      root.style.setProperty('--color-primary-gradient-to', hexToHsl(settings.primary_gradient_to))
    } else if (settings.primary_color) {
      // Fallback to primary color
      root.style.setProperty('--color-primary-gradient-to', hexToHsl(settings.primary_color))
    }
    
    if (settings.secondary_color) {
      root.style.setProperty('--color-secondary', hexToHsl(settings.secondary_color))
    }
    
    // Only try to set gradient variables if they exist in settings
    if (settings.secondary_gradient_from) {
      root.style.setProperty('--color-secondary-gradient-from', hexToHsl(settings.secondary_gradient_from))
    } else if (settings.secondary_color) {
      // Fallback to secondary color
      root.style.setProperty('--color-secondary-gradient-from', hexToHsl(settings.secondary_color))
    }
    
    if (settings.secondary_gradient_to) {
      root.style.setProperty('--color-secondary-gradient-to', hexToHsl(settings.secondary_gradient_to))
    } else if (settings.secondary_color) {
      // Fallback to secondary color
      root.style.setProperty('--color-secondary-gradient-to', hexToHsl(settings.secondary_color))
    }
    
    if (settings.accent_color) {
      root.style.setProperty('--color-accent', hexToHsl(settings.accent_color))
    }

    // Apply background/text based on *active* theme - SWAP for light mode
    if (currentTheme === 'dark') {
      if (settings.background_color) {
        root.style.setProperty('--color-bg-main', hexToHsl(settings.background_color))
      }
      if (settings.text_color) {
        root.style.setProperty('--color-text-main', hexToHsl(settings.text_color))
      }
      
      // Apply secondary background/text colors
      if (settings.background_secondary) {
        root.style.setProperty('--color-bg-secondary', hexToHsl(settings.background_secondary))
      } else if (settings.background_color) {
        // Fallback to a darker variant of main background
        const darkBg = createDarkerVariant(settings.background_color, 0.15)
        root.style.setProperty('--color-bg-secondary', hexToHsl(darkBg))
      }
      
      if (settings.text_secondary_color) {
        root.style.setProperty('--color-text-secondary', hexToHsl(settings.text_secondary_color))
      } else if (settings.text_color) {
        // Fallback to a lighter variant of main text
        const lightText = createLighterVariant(settings.text_color, 0.3)
        root.style.setProperty('--color-text-secondary', hexToHsl(lightText))
      }
    } else { // Light mode
      // Swap: Dark mode text becomes light mode background
      if (settings.text_color) {
        root.style.setProperty('--color-bg-main', hexToHsl(settings.text_color))
      }
      // Swap: Dark mode background becomes light mode text
      if (settings.background_color) {
        root.style.setProperty('--color-text-main', hexToHsl(settings.background_color))
      }
      
      // ALSO SWAP the secondary colors
      if (settings.text_secondary_color) {
        // Dark mode text_secondary becomes light mode bg_secondary
        root.style.setProperty('--color-bg-secondary', hexToHsl(settings.text_secondary_color))
      } else if (settings.text_color) {
        // Fallback to a lighter variant of main background (which is dark mode text)
        const lightBg = createLighterVariant(settings.text_color, 0.15)
        root.style.setProperty('--color-bg-secondary', hexToHsl(lightBg))
      }
      
      if (settings.background_secondary) {
        // Dark mode bg_secondary becomes light mode text_secondary
        root.style.setProperty('--color-text-secondary', hexToHsl(settings.background_secondary))
      } else if (settings.background_color) {
        // Fallback to a darker variant of main text (which is dark mode background)
        const darkText = createDarkerVariant(settings.background_color, 0.3)
        root.style.setProperty('--color-text-secondary', hexToHsl(darkText))
      }
    }
    
    // Color variant generation for HSL
    const createHslVariants = (color: string, prefix: string) => {
      try {
        // Ensure color is a valid hex code
        if (!/^#[0-9A-F]{6}$/i.test(color)) {
          console.warn(`Invalid color format for ${prefix}: ${color}`);
          // Use fallback
          root.style.setProperty(`${prefix}-light`, hexToHsl(color)); // Default to original
          root.style.setProperty(`${prefix}-dark`, hexToHsl(color)); // Default to original
          return;
        }

        // Convert hex to RGB for manipulation
        const r = parseInt(color.slice(1, 3), 16)
        const g = parseInt(color.slice(3, 5), 16)
        const b = parseInt(color.slice(5, 7), 16)
        
        // Lighter variant (blend with white)
        const lightR = Math.min(255, Math.round(r + (255 - r) * 0.3))
        const lightG = Math.min(255, Math.round(g + (255 - g) * 0.3))
        const lightB = Math.min(255, Math.round(b + (255 - b) * 0.3))
        
        // Darker variant (blend with black)
        const darkR = Math.round(r * 0.7)
        const darkG = Math.round(g * 0.7)
        const darkB = Math.round(b * 0.7)
        
        // Convert to HSL and set CSS variables
        const lightHex = `#${lightR.toString(16).padStart(2, '0')}${lightG.toString(16).padStart(2, '0')}${lightB.toString(16).padStart(2, '0')}`;
        const darkHex = `#${darkR.toString(16).padStart(2, '0')}${darkG.toString(16).padStart(2, '0')}${darkB.toString(16).padStart(2, '0')}`;
        
        root.style.setProperty(`${prefix}-light`, hexToHsl(lightHex))
        root.style.setProperty(`${prefix}-dark`, hexToHsl(darkHex))
      } catch (e) {
        console.error(`Error processing color variants for ${prefix}:`, e);
        // Fallback in case of unexpected errors
        root.style.setProperty(`${prefix}-light`, hexToHsl(color));
        root.style.setProperty(`${prefix}-dark`, hexToHsl(color));
      }
    }
    
    if (settings.primary_color) {
      createHslVariants(settings.primary_color, '--color-primary')
    }
    if (settings.secondary_color) {
      createHslVariants(settings.secondary_color, '--color-secondary')
    }
    if (settings.accent_color) {
      createHslVariants(settings.accent_color, '--color-accent')
    }
    
    // Set status colors if they exist in settings
    if (settings.status_error_color) {
      root.style.setProperty('--color-status-error', hexToHsl(settings.status_error_color))
    } else {
      // Fallback to a default red color
      root.style.setProperty('--color-status-error', hexToHsl('#ef4444'))
    }
    
    if (settings.status_error_light_color) {
      root.style.setProperty('--color-status-error-light', hexToHsl(settings.status_error_light_color))
    } else if (settings.status_error_color) {
      // Create lighter variant from the error color
      const lightErrorColor = createLighterVariant(settings.status_error_color, 0.7)
      root.style.setProperty('--color-status-error-light', hexToHsl(lightErrorColor))
    } else {
      // Fallback to a default light red color
      root.style.setProperty('--color-status-error-light', hexToHsl('#fee2e2'))
    }
    
    // Set surface colors based on current theme
    if (currentTheme === 'dark') {
      // Dark mode surface colors
      if (settings.surface_color) {
        root.style.setProperty('--color-surface', hexToHsl(settings.surface_color))
      } else if (settings.background_color) {
        // Default dark surface - slightly lighter than background
        const darkSurface = createLighterVariant(settings.background_color, 0.08)
        root.style.setProperty('--color-surface', hexToHsl(darkSurface))
      }
      
      if (settings.surface_hover_color) {
        root.style.setProperty('--color-surface-hover', hexToHsl(settings.surface_hover_color))
      } else {
        // Default dark surface hover - DARKER than surface (opposite of current)
        if (settings.surface_color) {
          const darkSurfaceHover = createDarkerVariant(settings.surface_color, 0.08)
          root.style.setProperty('--color-surface-hover', hexToHsl(darkSurfaceHover))
        } else if (settings.background_color) {
          const darkSurfaceHover = createDarkerVariant(settings.background_color, 0.08)  
          root.style.setProperty('--color-surface-hover', hexToHsl(darkSurfaceHover))
        }
      }
      
      if (settings.surface_active_color) {
        root.style.setProperty('--color-surface-active', hexToHsl(settings.surface_active_color))
      } else {
        // Default dark surface active - even lighter than hover
        if (settings.surface_hover_color) {
          const darkSurfaceActive = createLighterVariant(settings.surface_hover_color, 0.08)
          root.style.setProperty('--color-surface-active', hexToHsl(darkSurfaceActive))
        } else if (settings.background_color) {
          const darkSurfaceActive = createLighterVariant(settings.background_color, 0.24)
          root.style.setProperty('--color-surface-active', hexToHsl(darkSurfaceActive))
        }
      }
    } else {
      // Light mode surface colors - use explicit light mode settings if available
      if (settings.light_mode_surface_color) {
        root.style.setProperty('--color-surface', hexToHsl(settings.light_mode_surface_color))
      } else if (settings.text_color) {
        // Default light surface - slightly darker than background
        const lightSurface = createDarkerVariant(settings.text_color, 0.08)
        root.style.setProperty('--color-surface', hexToHsl(lightSurface))
      }
      
      if (settings.light_mode_surface_hover_color) {
        root.style.setProperty('--color-surface-hover', hexToHsl(settings.light_mode_surface_hover_color))
      } else if (settings.text_color) {
        // Default light surface hover
        const lightSurfaceHover = createLighterVariant(settings.text_color, 0.08)
        root.style.setProperty('--color-surface-hover', hexToHsl(lightSurfaceHover))
      }
      
      if (settings.light_mode_surface_active_color) {
        root.style.setProperty('--color-surface-active', hexToHsl(settings.light_mode_surface_active_color))
      } else if (settings.text_color) {
        // Default light surface active
        const lightSurfaceActive = createDarkerVariant(settings.text_color, 0.24)
        root.style.setProperty('--color-surface-active', hexToHsl(lightSurfaceActive))
      }
    }
    
    // Set border colors
    if (settings.border_color) {
      root.style.setProperty('--color-border', hexToHsl(settings.border_color))
    } else {
      // Default border color based on theme
      if (currentTheme === 'dark' && settings.background_color) {
        const borderColor = createLighterVariant(settings.background_color, 0.2)
        root.style.setProperty('--color-border', hexToHsl(borderColor))
      } else if (currentTheme === 'light' && settings.text_color) {
        const borderColor = createDarkerVariant(settings.text_color, 0.2)
        root.style.setProperty('--color-border', hexToHsl(borderColor))
      }
    }
    
    if (settings.border_focus_color) {
      root.style.setProperty('--color-border-focus', hexToHsl(settings.border_focus_color))
    } else if (settings.primary_color) {
      // Default focus border is primary color
      root.style.setProperty('--color-border-focus', hexToHsl(settings.primary_color))
    }
    
    // Set static colors - these should be used regardless of theme
    if (settings.static_black_color && /^#[0-9A-F]{6}$/i.test(settings.static_black_color)) {
      root.style.setProperty('--color-static-black', hexToHsl(settings.static_black_color))
    } else {
      // Fallback to pure black
      root.style.setProperty('--color-static-black', '0 0% 0%') // HSL for black
    }
    
    if (settings.static_white_color && /^#[0-9A-F]{6}$/i.test(settings.static_white_color)) {
      root.style.setProperty('--color-static-white', hexToHsl(settings.static_white_color))
    } else {
      // Fallback to pure white
      root.style.setProperty('--color-static-white', '0 0% 100%') // HSL for white
    }
    
    // Set other status colors (success, warning, info) if they exist
    if (settings.status_success_color) {
      root.style.setProperty('--color-status-success', hexToHsl(settings.status_success_color))
    } else {
      root.style.setProperty('--color-status-success', hexToHsl('#22c55e'))
    }
    
    if (settings.status_warning_color) {
      root.style.setProperty('--color-status-warning', hexToHsl(settings.status_warning_color))
    } else {
      root.style.setProperty('--color-status-warning', hexToHsl('#f59e0b'))
    }
    
    if (settings.status_info_color) {
      root.style.setProperty('--color-status-info', hexToHsl(settings.status_info_color))
    } else {
      root.style.setProperty('--color-status-info', hexToHsl('#3b82f6'))
    }

  }, [themePreference, settings, mounted, forcedTheme, isLocaleRoot, isProductsListing, pathname, locale])

  // Effect to listen for system theme changes when preference is 'system'
  useEffect(() => {
    // Skip listener if forced or preference not system or not mounted
    if (forcedTheme || themePreference !== 'system' || !mounted) return

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    const handleChange = (e: MediaQueryListEvent) => {
      setActiveTheme(e.matches ? 'dark' : 'light')
    }

    // Modern event listener API
    mediaQuery.addEventListener('change', handleChange)
    return () => mediaQuery.removeEventListener('change', handleChange)
  }, [themePreference, mounted, forcedTheme])

  // Function to update the theme preference
  const handleSetTheme = (newPreference: 'light' | 'dark' | 'system') => {
    setThemePreference(newPreference)
    localStorage.setItem('themePreference', newPreference)
  }

  // Create the context value with safe defaults for SSR
  const contextValue = {
    settings,
    // Use theme from state or safe default for SSR (dark)
    theme: mounted && activeTheme ? activeTheme : 'dark',
    // Use preference from state or safe default for SSR (system)
    themePreference: mounted && themePreference ? themePreference : 'system',
    setTheme: handleSetTheme
  };

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  )
}

// Custom hook to use the theme context
export function useTheme() {
  const context = useContext(ThemeContext)
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return context
}

// Helper functions for color variants
function createLighterVariant(hexColor: string, amount: number): string {
  try {
    if (!/^#[0-9A-F]{6}$/i.test(hexColor)) {
      return hexColor;
    }
    
    const r = parseInt(hexColor.slice(1, 3), 16)
    const g = parseInt(hexColor.slice(3, 5), 16)
    const b = parseInt(hexColor.slice(5, 7), 16)
    
    const lightR = Math.min(255, Math.round(r + (255 - r) * amount))
    const lightG = Math.min(255, Math.round(g + (255 - g) * amount))
    const lightB = Math.min(255, Math.round(b + (255 - b) * amount))
    
    return `#${lightR.toString(16).padStart(2, '0')}${lightG.toString(16).padStart(2, '0')}${lightB.toString(16).padStart(2, '0')}`
  } catch {
    // Return original color if any error occurs
    return hexColor;
  }
}

function createDarkerVariant(hexColor: string, amount: number): string {
  try {
    if (!/^#[0-9A-F]{6}$/i.test(hexColor)) {
      return hexColor;
    }
    
    const r = parseInt(hexColor.slice(1, 3), 16)
    const g = parseInt(hexColor.slice(3, 5), 16)
    const b = parseInt(hexColor.slice(5, 7), 16)
    
    const darkR = Math.round(r * (1 - amount))
    const darkG = Math.round(g * (1 - amount))
    const darkB = Math.round(b * (1 - amount))
    
    return `#${darkR.toString(16).padStart(2, '0')}${darkG.toString(16).padStart(2, '0')}${darkB.toString(16).padStart(2, '0')}`
  } catch {
    // Return original color if any error occurs
    return hexColor;
  }
} 